import React, { useState, useEffect } from 'react';
import { X, Users } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { xTagListsAPI, XTagList } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';

interface XTagListPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the list's normalized handles (no @). The composer appends them. */
  onApply: (handles: string[]) => void;
}

const XTagListPickerModal: React.FC<XTagListPickerModalProps> = ({ isOpen, onClose, onApply }) => {
  const { sessionToken } = useAuth();
  const { isMobile } = useResponsive();
  const [tagLists, setTagLists] = useState<XTagList[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && sessionToken) loadTagLists();
  }, [isOpen, sessionToken]);

  const loadTagLists = async () => {
    if (!sessionToken) return;
    setIsLoading(true);
    try {
      const res = await xTagListsAPI.getTagLists(sessionToken);
      if (res.success) setTagLists(res.tagLists);
    } catch (error) {
      console.error('Error loading tag lists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = (list: XTagList) => {
    onApply(list.handles || []);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10003, padding: isMobile ? '16px' : '20px' }}
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '16px', width: isMobile ? 'calc(100vw - 32px)' : '100%', maxWidth: isMobile ? 'calc(100vw - 32px)' : '480px', maxHeight: isMobile ? 'calc(100vh - 100px)' : '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1F2937' }}>Add a Tag List</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>Loading tag lists...</div>
          ) : tagLists.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '16px' }}>No tag lists yet</p>
              <p style={{ margin: 0, fontSize: '14px' }}>Create one in Account Settings → Tag Lists to reuse the accounts you tag most.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tagLists.map((list) => (
                <button
                  key={list.id}
                  onClick={() => handleApply(list)}
                  style={{ width: '100%', padding: '16px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; e.currentTarget.style.borderColor = '#D1D5DB'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <Users size={20} color="#6B7280" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 600, color: '#1F2937' }}>{list.name}</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(list.handles || []).length > 0
                          ? (list.handles || []).slice(0, 8).map((h) => `@${h}`).join(' ') + ((list.handles || []).length > 8 ? ` +${list.handles.length - 8}` : '')
                          : 'No handles'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default XTagListPickerModal;
